/**
 * Shared Article Page Content Component
 * Used by /news/[slug], /guides/[slug], and /analysis/[slug] routes
 * Provides consistent article rendering across all content types
 *
 * F6 reading redesign (Stage 1 exemplar): content-forward read in the dark
 * shell. Article column (~720px readable measure) + a right-hand CONTEXT
 * COLUMN (entities in this piece + more on these). The piece IS the conclusion
 * — no synthetic assessment header, no claps, no author link, no market-watch.
 */

'use client';

import React from 'react';
import { safeJsonLd } from '@/lib/json-ld';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { ArrowLeft } from 'lucide-react';
import { Article, User } from '@/types/content';
import { Tag } from '@/types/tags';
import { tagService } from '@/lib/tag-service';
import DeskByline from '@/components/ui/DeskByline';
import { viaOutlet, type Provenance } from '@/lib/provenance';
import { isReceiptEligible, receiptPagePath, type RawDoc } from '@/lib/receipts';
import ShareButtons from '@/components/ui/ShareButtons';
import NewsletterSignup from '@/components/ui/NewsletterSignup';
import TableOfContents from '@/components/ui/TableOfContents';
import ArticleViewTracker from '@/components/analytics/ArticleViewTracker';
import SocialShare from '@/components/SocialShare';
import { getArticleAbsoluteUrl, getContentTypePrefix, getArticlePath } from '@/lib/article-url-utils';
import { formatRelativeTime } from '@/utils/date-formatting';
import 'highlight.js/styles/github-dark.css';

import ArticleAdSpot from '@/components/ui/ArticleAdSpot';
import { TagMention } from '@/components/ui/TagMention';
import { TokenSymbol } from '@/components/ui/TokenSymbol';
import { MentionDedupeProvider } from '@/components/ui/MentionDedupe';
import { parseTextWithMentions, extractTwitterHandleFromHref } from '@/utils/markdown-utils';
import PreviewBanner from '@/components/ui/PreviewBanner';
import PieceContext, {
  PieceContextEntity,
  PieceContextMore,
} from '@/components/pages/reading/PieceContext';
import ArticleEndQuestion from '@/components/questions/ArticleEndQuestion';
import CorrectionNotice from '@/components/content/CorrectionNotice';
import type { SupersededLink } from '@/lib/corrections';
import type { OpenQuestion } from '@/types/open-questions';
import EntityTrajectoryStub, {
  type EntityTrajectoryStubData,
} from '@/components/trajectory/EntityTrajectoryStub';

// Dynamic imports
const ClientImageWithCaption = dynamic(
  () => import('@/components/ui/ClientImageWithCaption'),
  {
    ssr: false,
    loading: () => (
      <span className="block my-6" style={{ width: '100%', margin: '1.5rem 0' }}>
        <span
          className="inline-block bg-surface2 animate-pulse rounded-lg"
          style={{
            width: '100%',
            height: '200px',
            display: 'block'
          }}
        />
      </span>
    )
  }
);

const TwitterEmbed = dynamic(
  () => import('@/components/ui/SocialEmbed').then(mod => ({ default: mod.TwitterEmbed })),
  { ssr: false }
);

const YouTubeEmbed = dynamic(
  () => import('@/components/ui/SocialEmbed').then(mod => ({ default: mod.YouTubeEmbed })),
  { ssr: false }
);

const PodBeanEmbed = dynamic(
  () => import('@/components/ui/PodBeanEmbed'),
  { ssr: false }
);

interface ArticlePageContentProps {
  article: Article;
  authorData: User | null;
  relatedArticles: Article[];
  processedContent: string;
  newsArticleSchema: any;
  breadcrumbSchema: any;
  isPreviewMode?: boolean;
  /** Article-end open-question hook. null/absent = render nothing (byte-neutral when flag off). */
  endQuestion?: OpenQuestion | null;
  /** Programmatic subject-entity trajectory. null/absent = render nothing (no subject / untracked). */
  trajectory?: EntityTrajectoryStubData | null;
  /** R3 corrections desk: server-resolved forward pointer when the article was superseded. */
  supersededLink?: SupersededLink | null;
  /**
   * CS1b — server-resolved honest provenance WITH the gold verdict AND the masthead identity map
   * wired in (resolveContentProvenance). REQUIRED, deliberately: this used to be optional with a
   * `?? deriveProvenance(doc, {kind})` fallback, which looked harmless but re-derived with neither
   * the gold verdict nor any identity — a detail route that forgot the prop would have silently
   * under-claimed to grammar B on a piece with a provable human reviewer. Making it required moves
   * that from "silent forever" to "will not compile".
   */
  provenance: Provenance;
}

/** Pick the best thumbnail for an entity card (primary image, else first). */
function entityImage(tag: Tag): string | undefined {
  if (!tag.images || tag.images.length === 0) return undefined;
  const primary = tag.images.find((img) => img.isPrimary);
  return (primary || tag.images[0])?.url;
}

export default function ArticlePageContent({
  article,
  authorData,
  relatedArticles,
  processedContent,
  newsArticleSchema,
  breadcrumbSchema,
  isPreviewMode = false,
  endQuestion = null,
  trajectory = null,
  supersededLink = null,
  provenance,
}: ArticlePageContentProps) {
  // CONTEXT COLUMN: entities resolved from article.tags[] -> dossier cards.
  const [contextEntities, setContextEntities] = React.useState<PieceContextEntity[]>([]);

  React.useEffect(() => {
    let cancelled = false;

    const resolveEntities = async () => {
      const tagIds = (article.tags || []).slice(0, 12); // cap fetch; we display ~6
      if (tagIds.length === 0) {
        setContextEntities([]);
        return;
      }

      try {
        const tagMap = await tagService.getTagsBatch(tagIds);
        if (cancelled) return;

        // Preserve article.tags[] order; only entity tags (typed) are graph hooks.
        const resolved: PieceContextEntity[] = [];
        for (const id of tagIds) {
          const tag = tagMap.get(id);
          if (!tag || tag.isActive === false) continue;
          if (!tag.entityType) continue; // descriptive tags are not dossiers
          resolved.push({
            slug: tag.slug,
            name: tag.name,
            image: entityImage(tag),
            type: tag.entityType || undefined,
          });
          if (resolved.length >= 6) break;
        }

        setContextEntities(resolved);
      } catch (err) {
        console.error('Failed to resolve entities for context column:', err);
        if (!cancelled) setContextEntities([]);
      }
    };

    resolveEntities();
    return () => {
      cancelled = true;
    };
  }, [article.id]);

  // CONTEXT COLUMN: "MORE ON THESE" — same-entity pieces ranked by RECENCY
  // (not claps). Uses the relatedArticles prop (already tag-overlap filtered),
  // re-sorted by publishedAt desc, current article excluded, capped at 5.
  const moreOnThese: PieceContextMore[] = React.useMemo(() => {
    return (relatedArticles || [])
      .filter((a) => a.id !== article.id && a.slug !== article.slug)
      .slice()
      .sort((a, b) => {
        const da = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const dbt = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        return dbt - da;
      })
      .slice(0, 5)
      .map((a) => {
        const typeLabel =
          a.contentType === 'guide'
            ? 'Guide'
            : a.contentType === 'analysis'
              ? 'Analysis'
              : 'News';
        const time = a.publishedAt ? formatRelativeTime(a.publishedAt) : '';
        return {
          href: getArticlePath(a),
          title: a.title,
          eyebrow: time ? `${typeLabel} · ${time}` : typeLabel,
        };
      });
  }, [relatedArticles, article.id, article.slug]);

  // Helper functions for heading ID generation
  const extractTextFromChildren = (children: any): string => {
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join('');
    }
    if (children?.props?.children) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };

  const generateHeadingId = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const contentTypePrefix = getContentTypePrefix(article.contentType);
  const backLink = `/${contentTypePrefix}`;
  const backLinkText = article.contentType === 'guide' ? 'Back to Guides' :
    article.contentType === 'analysis' ? 'Back to Analysis' :
      'Back to News';

  // Eyebrow: TYPE · TIME · CHAIN (real-data-only on the chain segment).
  const typeLabel = article.contentType === 'guide' ? 'Guide' :
    article.contentType === 'analysis' ? 'Analysis' : 'News';
  const publishedLabel = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    : 'Draft';

  return (
    <>
      {/* Analytics Tracking */}
      <ArticleViewTracker
        title={article.title}
        slug={article.slug}
        category={article.category}
        tags={article.tags}
        contentType={article.contentType}
        author={authorData?.displayName}
      />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(newsArticleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(breadcrumbSchema) }}
      />

      {/* Preview Banner - shown for non-public articles */}
      {isPreviewMode && (
        <PreviewBanner
          visibility={article.visibility}
          status={article.status}
        />
      )}

      {/*
        F6 reading layout: the DashboardShell already provides the
        max-w-[1760px] container + gutters, so we do NOT re-impose
        max-w-screen-2xl. Two-column grid (article + context) capped and
        centered so the read keeps a comfortable measure and the context
        column is a true sidebar — not a column that balloons to fill all
        remaining desktop width. Collapses to one column on mobile (context
        stacks below). Cap math: ~720 article + 320 sidebar + 56px (gap-14).
      */}
      <div className="py-8 lg:py-10">
        <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-14">
          {/* ARTICLE COLUMN */}
          <div className="min-w-0">
            <article>
              {/* Back link */}
              <div className="mb-6">
                <Link
                  href={backLink}
                  className="inline-flex items-center font-mono text-[11px] uppercase tracking-[0.14em] text-parchment-dim transition-colors hover:text-scout-gold"
                >
                  <ArrowLeft className="mr-2 h-3.5 w-3.5" />
                  {backLinkText}
                </Link>
              </div>

              {/* Eyebrow: type · time · chain (the header block is gone; the
                  title lives in the body) */}
              <div className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase tracking-[0.14em] text-parchment-dim">
                <span className="text-scout-gold">{typeLabel}</span>
                <span>·</span>
                <time dateTime={article.publishedAt?.toISOString()}>{publishedLabel}</time>
                <span>·</span>
                <span>{article.readTime || 1} min read</span>
              </div>

              {/* Title — in the body, content-forward */}
              <h1 className="mb-4 font-display text-3xl font-bold leading-[1.15] tracking-[-0.02em] text-parchment md:text-4xl lg:text-[2.75rem]">
                {article.title}
              </h1>

              {/* Excerpt / standfirst */}
              {article.excerpt && (
                <p className="mb-5 font-body text-lg leading-relaxed text-parchment-dim md:text-xl">
                  {article.excerpt}
                </p>
              )}

              {/* CS1 — honest byline: producer lane + (only when a real human verdict record
                  backs it) the reviewer's masthead identity; else the auto-published grammar. Derived from
                  pipeline provenance, NEVER the author field (the author-field interlock).
                  CS2 — a desk brief additionally carries a "via {Outlet}" source attribution. */}
              {(() => {
                // Server-resolved verbatim — there is no local fallback to under-claim through.
                const prov = provenance;
                return (
                  <DeskByline
                    provenance={prov}
                    via={viaOutlet(article, prov) ?? undefined}
                    receiptHref={
                      article.id && isReceiptEligible(article as unknown as RawDoc, 'article')
                        ? receiptPagePath('article', article.id)
                        : undefined
                    }
                  />
                );
              })()}

              {/* Share buttons (claps removed) */}
              <div className="mb-8 border-y-[0.5px] border-divider py-4">
                <ShareButtons
                  title={article.title}
                  url={getArticleAbsoluteUrl(article)}
                />
              </div>

              {/* Corrections desk (R3): dated notice when this piece was corrected or
                  superseded. Renders NOTHING when uncorrected — byte-identical to before. */}
              <CorrectionNotice
                correction={article.correction}
                transitionLog={article.transitionLog}
                supersededLink={supersededLink}
              />

              {/* Table of Contents — compact, mobile only (desktop relies on
                  the reading measure; keeping it inline avoids the dead
                  sidebar) */}
              <div className="mb-8 lg:hidden">
                <TableOfContents />
              </div>

              {/* Featured Image */}
              {article.featuredImage && (
                <div className="mb-8">
                  <img
                    src={article.featuredImage}
                    alt={`${article.title} - ${article.contentType === 'guide' ? 'Tutorial and guide' : article.contentType === 'analysis' ? 'Market analysis' : 'Analysis and insights'}`}
                    className="h-64 w-full rounded-lg border-[0.5px] border-divider object-cover md:h-96"
                  />
                </div>
              )}

              {/* Podcast Embed - Appears before content if available */}
              {article.podcastEmbedUrl && (
                <div className="mb-8">
                  <PodBeanEmbed
                    embedUrl={article.podcastEmbedUrl}
                    title={article.title}
                    caption="Listen to this article"
                  />
                </div>
              )}

              {/* Article Content — the markdown pipeline is preserved verbatim;
                  only the prose color classes move to the dark palette.
                  MentionDedupeProvider scopes first-mention image-chips to this
                  one document (repeats render as plain entity links). */}
              <div className="prose prose-invert prose-lg max-w-none break-words">
                <MentionDedupeProvider>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight, rehypeRaw]}
                  components={{
                    h1: ({ children }) => {
                      const text = extractTextFromChildren(children);
                      const id = generateHeadingId(text);
                      return (
                        <h1 id={id} className="mb-6 mt-8 scroll-mt-24 font-display text-3xl font-bold text-parchment first:mt-0">
                          {children}
                        </h1>
                      );
                    },
                    h2: ({ children }) => {
                      const text = extractTextFromChildren(children);
                      const id = generateHeadingId(text);
                      return (
                        <h2 id={id} className="mb-4 mt-8 scroll-mt-24 border-b-[0.5px] border-divider pb-2 font-display text-2xl font-bold text-parchment">
                          {children}
                        </h2>
                      );
                    },
                    h3: ({ children }) => {
                      const text = extractTextFromChildren(children);
                      const id = generateHeadingId(text);
                      return (
                        <h3 id={id} className="mb-3 mt-6 scroll-mt-24 font-display text-xl font-bold text-parchment">
                          {children}
                        </h3>
                      );
                    },
                    p: ({ children, ...props }: any) => {
                      const childArray = Array.isArray(children) ? children : [children];
                      // Check if this paragraph only contains an image/figure element
                      if (childArray.length === 1) {
                        const child = childArray[0];
                        if (child && typeof child === 'object') {
                          // Check for img type, figure type, or any component that might be an image
                          const childType = child.type;
                          const isImageComponent =
                            childType === 'img' ||
                            childType === 'figure' ||
                            (child.props && (child.props.src || child.props.videoId || child.props.tweetUrl || child.props.embedUrl));

                          if (isImageComponent) {
                            return <>{children}</>;
                          }
                        }
                      }

                      // Parse @mentions and $symbols in children
                      const childrenWithMentions = React.Children.map(children, child => {
                        if (typeof child === 'string') {
                          const segments = parseTextWithMentions(child);
                          return segments.map((segment, index) => {
                            if (segment.type === 'mention') {
                              return <TagMention key={`mention-${index}`} slug={segment.value} />;
                            }
                            if (segment.type === 'symbol') {
                              return <TokenSymbol key={`symbol-${index}`} symbol={segment.value} />;
                            }
                            return segment.value;
                          });
                        }
                        return child;
                      });

                      return (
                        <p className="mb-4 break-words font-body text-lg leading-relaxed text-parchment/90" {...props}>
                          {childrenWithMentions}
                        </p>
                      );
                    },
                    a: ({ href, children }) => {
                      // Inline twitter/x.com profile links (e.g. draft generators
                      // emit `[@handle](https://x.com/handle)`) bypass the @mention
                      // parser. Route them back through the entity chip: TagMention
                      // resolves by slug-or-handle, renders the rich chip when a tag
                      // exists (de-duped to first mention), and falls back to plain
                      // text when none — so untagged handles stay plain. Skip when
                      // the link text isn't a bare @handle (e.g. a sentence linked
                      // to a profile) to avoid swallowing real link copy.
                      const twitterHandle = extractTwitterHandleFromHref(href);
                      const linkText =
                        typeof children === 'string'
                          ? children
                          : Array.isArray(children) && children.length === 1 && typeof children[0] === 'string'
                            ? children[0]
                            : null;
                      if (twitterHandle && linkText && /^@?[A-Za-z0-9_]{1,15}$/.test(linkText.trim())) {
                        return <TagMention slug={twitterHandle} />;
                      }

                      const isExternal = href?.startsWith('http');
                      const isInternal = href?.startsWith('/') || href?.startsWith('#');

                      // Internal links use Next.js Link for SPA navigation
                      if (isInternal && !href?.startsWith('#')) {
                        return (
                          <Link
                            href={href || '/'}
                            className="break-words text-scout-gold underline transition-colors hover:text-scout-gold/80"
                          >
                            {children}
                          </Link>
                        );
                      }

                      // External links: nofollow to preserve PageRank, except allowlisted domains
                      const allowedDomains = ['abstract.money', 'abs.xyz', 'docs.abs.xyz'];
                      const isAllowedDomain = allowedDomains.some(domain => href?.includes(domain));

                      return (
                        <a
                          href={href}
                          className="break-words text-scout-gold underline transition-colors hover:text-scout-gold/80"
                          target={isExternal ? '_blank' : undefined}
                          rel={isExternal ? (isAllowedDomain ? 'noopener' : 'nofollow noopener') : undefined}
                        >
                          {children}
                        </a>
                      );
                    },
                    blockquote: ({ children }) => {
                      const childrenWithMentions = React.Children.map(children, child => {
                        if (typeof child === 'string') {
                          const segments = parseTextWithMentions(child);
                          return segments.map((segment, index) => {
                            if (segment.type === 'mention') {
                              return <TagMention key={`mention-${index}`} slug={segment.value} />;
                            }
                            if (segment.type === 'symbol') {
                              return <TokenSymbol key={`symbol-${index}`} symbol={segment.value} />;
                            }
                            return segment.value;
                          });
                        }
                        return child;
                      });
                      return (
                        <blockquote className="my-6 rounded-r-lg border-l-4 border-scout-gold bg-surface py-4 pl-6 italic text-parchment-dim">
                          {childrenWithMentions}
                        </blockquote>
                      );
                    },
                    code: ({ children, className }) => {
                      const isInline = !className;
                      if (isInline) {
                        return (
                          <code className="break-all rounded bg-surface2 px-2 py-1 font-mono text-sm text-parchment">
                            {children}
                          </code>
                        );
                      }
                      return (
                        <code className={className}>
                          {children}
                        </code>
                      );
                    },
                    pre: ({ children }) => (
                      <pre className="my-6 overflow-x-auto rounded-lg border-[0.5px] border-divider bg-surface p-4 text-parchment">
                        {children}
                      </pre>
                    ),
                    ul: ({ children }) => (
                      <ul className="my-4 ml-6 list-outside list-disc space-y-2 pl-2 text-parchment/90">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="my-4 ml-6 list-outside list-decimal space-y-2 pl-2 text-parchment/90">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => {
                      const childrenWithMentions = React.Children.map(children, child => {
                        if (typeof child === 'string') {
                          const segments = parseTextWithMentions(child);
                          return segments.map((segment, index) => {
                            if (segment.type === 'mention') {
                              return <TagMention key={`mention-${index}`} slug={segment.value} />;
                            }
                            return segment.value;
                          });
                        }
                        return child;
                      });
                      return (
                        <li className="break-words leading-relaxed text-parchment/90">{childrenWithMentions}</li>
                      );
                    },
                    img: ({ src, alt, ...props }: any) => {
                      let width: string | undefined = undefined;
                      let cleanSrc = src || '';

                      const encodedMatch = cleanSrc.match(/%7Bwidth=(\d+(?:%|px)?)%7D$/i);
                      if (encodedMatch) {
                        width = encodedMatch[1];
                        cleanSrc = cleanSrc.replace(/%7Bwidth=\d+(?:%|px)?%7D$/i, '');
                      } else {
                        const plainMatch = cleanSrc.match(/\{width=(\d+(?:%|px)?)\}$/);
                        if (plainMatch) {
                          width = plainMatch[1];
                          cleanSrc = cleanSrc.replace(/\{width=\d+(?:%|px)?\}$/, '');
                        }
                      }

                      cleanSrc = cleanSrc.replace(/<!--width:\d+(?:%|px)?-->$/, '');
                      cleanSrc = cleanSrc.replace(/%3C!--width:\d+(?:%|px)?--%3E$/i, '');

                      const youtubeMatch = cleanSrc.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
                      if (youtubeMatch) {
                        const videoId = youtubeMatch[1];
                        return <YouTubeEmbed videoId={videoId} caption={alt} />;
                      }

                      const twitterMatch = cleanSrc.match(/(?:twitter\.com|x\.com)\/[\w]+\/status\/(\d+)/);
                      if (twitterMatch) {
                        const normalizedUrl = cleanSrc.replace(/x\.com/, 'twitter.com');
                        return <TwitterEmbed tweetUrl={normalizedUrl} caption={alt} />;
                      }

                      const podbeanMatch = cleanSrc.match(/podbean\.com/i);
                      if (podbeanMatch) {
                        return <PodBeanEmbed embedUrl={cleanSrc} caption={alt} />;
                      }

                      return <ClientImageWithCaption src={cleanSrc} alt={alt} width={width} />;
                    },
                  }}
                >
                  {processedContent}
                </ReactMarkdown>
                </MentionDedupeProvider>
              </div>

              {/* No PROMOTED SOURCE block on articles: they are original authored
                  pieces with no external source-of-record (the byline already
                  attributes the author). The shared PromotedSource component is
                  used by the breaking-news reader, where a real source tweet exists. */}

              {/* Social Share Buttons */}
              <div className="mt-8 border-t-[0.5px] border-divider pt-6">
                <SocialShare
                  url={getArticleAbsoluteUrl(article)}
                  title={article.title}
                  contentType="article"
                  variant="inline"
                />
              </div>

              {/* Article Footer Ad Spot */}
              <ArticleAdSpot location="article-footer" />

              {/* Programmatic trajectory stub — the data-driven replacement for
                  the banned "Descout tracks X as Building" writer prose. Renders
                  nothing when there's no subject entity or no trajectory doc. */}
              <EntityTrajectoryStub data={trajectory ?? null} />

              {/* Article-end open question hook (Session C #4).
                  Renders nothing when endQuestion is null — byte-neutral when flag off. */}
              <ArticleEndQuestion question={endQuestion ?? null} />
            </article>
          </div>

          {/* CONTEXT COLUMN — entities in this piece + more on these.
              On mobile this stacks below the article (single column grid). */}
          <aside className="min-w-0">
            <div className="lg:sticky lg:top-8 space-y-10">
              <PieceContext entities={contextEntities} more={moreOnThese} />

              {/* Table of Contents — Desktop only, lives in the context rail */}
              <div className="hidden lg:block">
                <TableOfContents />
              </div>

              {/* Newsletter Signup */}
              <NewsletterSignup />

              {/* Context-rail Ad Spot */}
              <div className="hidden lg:block">
                <ArticleAdSpot location="article-sidebar" />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}
